// frontend/src/pages/admin/ContractSlaManagementModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Form, InputNumber, Select, Button, Space, Popconfirm, Spin, Row, Col } from 'antd';
import { CheckOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const { Option } = Select;

const ContractSlaManagementModal = ({ visible, onCancel, contractId, contractName }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [ticketCategories, setTicketCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [customSlas, setCustomSlas] = useState({});

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    const minDisplayTime = 500;
    const startTime = Date.now();
    notificationApi.open({
      key,
      message: messageContent,
      duration: 0,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });

    return () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      setTimeout(() => {
        notificationApi.destroy(key);
      }, remaining);
    };
  }, [notificationApi]);

  const fetchData = useCallback(async () => {
    if (!contractId) return;

    setLoading(true);
    const loadingKey = 'fetchSlaDataLoading';
    const hideLoading = showLoading(loadingKey, 'Loading SLA data...');

    try {
      const categoriesRes = await api.get('/api/ticket-categories');
      const categories = categoriesRes.data?.data || [];
      const prioritiesRes = await api.get('/api/priorities');
      const fetchedPriorities = prioritiesRes.data?.data || [];
      setPriorities(fetchedPriorities);
      setTicketCategories(categories);

      const initialSlas = {};
      const fieldsToSet = {};

      await Promise.all(
        categories.map(async (category) => {
          try {
            const slaRes = await api.get(`/api/contracts/${contractId}/categories/${category.id}/sla`);
            const slaData = slaRes.data?.data;
            if (slaData) {
              initialSlas[category.id] = { ...slaData, isSaved: true };
              fieldsToSet[`agreedResponseTimeValue-${category.id}`] = slaData.agreedResponseTimeValue;
              fieldsToSet[`agreedResponseTimeUnit-${category.id}`] = slaData.agreedResponseTimeUnit;
              fieldsToSet[`agreedResolutionTimeValue-${category.id}`] = slaData.agreedResolutionTimeValue;
              fieldsToSet[`agreedResolutionTimeUnit-${category.id}`] = slaData.agreedResolutionTimeUnit;
              fieldsToSet[`priorityId-${category.id}`] = slaData.priorityId;
            } else {
              initialSlas[category.id] = { isSaved: false };
            }
          } catch (error) {
            console.warn(`Failed to load SLA for category ${category.name}:`, error);
            initialSlas[category.id] = { isSaved: false };
          }
        })
      );

      setCustomSlas(initialSlas);
      form.setFieldsValue(fieldsToSet);
    } catch (error) {
      console.error('Failed to load SLA data:', error);
      showError('Failed to Load Data', error.response?.data?.message || 'An error occurred while loading SLA data.', 'fetchSlaDataError');
    } finally {
      hideLoading();
      setLoading(false);
    }
  }, [contractId, form, showLoading, showError]);

  useEffect(() => {
    if (visible) {
      fetchData();
    } else {
      setTicketCategories([]);
      setCustomSlas({});
      setSaving({});
      form.resetFields();
    }
  }, [visible, fetchData, form]);

  const handleSaveSla = async (categoryId) => {
    const loadingKey = `saveSla-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Saving SLA for category ${categoryId}...`);
    try {
      const values = await form.validateFields([
        `agreedResponseTimeValue-${categoryId}`,
        `agreedResponseTimeUnit-${categoryId}`,
        `agreedResolutionTimeValue-${categoryId}`,
        `agreedResolutionTimeUnit-${categoryId}`,
        `priorityId-${categoryId}`
      ]);

      setSaving(prev => ({ ...prev, [categoryId]: true }));
      
      const data = {
        agreedResponseTimeValue: values[`agreedResponseTimeValue-${categoryId}`],
        agreedResponseTimeUnit: values[`agreedResponseTimeUnit-${categoryId}`],
        agreedResolutionTimeValue: values[`agreedResolutionTimeValue-${categoryId}`],
        agreedResolutionTimeUnit: values[`agreedResolutionTimeUnit-${categoryId}`],
        priorityId: values[`priorityId-${categoryId}`],
      };

      await api.put(`/api/contracts/${contractId}/categories/${categoryId}/sla`, data);

      setCustomSlas(prev => ({
        ...prev,
        [categoryId]: { ...data, isSaved: true }
      }));
      hideLoading();
      showSuccess('SLA Saved', 'Custom SLA updated successfully!', `saveSlaSuccess-${categoryId}`);

    } catch (error) {
      hideLoading();
      console.error('Failed to save SLA:', error);
      showError('Failed to Save', error.response?.data?.message || 'An error occurred while saving the SLA.', `saveSlaError-${categoryId}`);
    } finally {
      setSaving(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const handleResetSla = async (categoryId) => {
    const loadingKey = `resetSla-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Resetting SLA for category ${categoryId}...`);
    try {
      setSaving(prev => ({ ...prev, [categoryId]: true }));
      await api.delete(`/api/contracts/${contractId}/categories/${categoryId}/sla`);

      setCustomSlas(prev => ({
        ...prev,
        [categoryId]: { isSaved: false }
      }));
      form.setFieldsValue({
        [`agreedResponseTimeValue-${categoryId}`]: undefined,
        [`agreedResponseTimeUnit-${categoryId}`]: undefined,
        [`agreedResolutionTimeValue-${categoryId}`]: undefined,
        [`agreedResolutionTimeUnit-${categoryId}`]: undefined,
        [`priorityId-${categoryId}`]: undefined,
      });
      hideLoading();
      showSuccess('SLA Reset', 'Custom SLA has been removed.', `resetSlaSuccess-${categoryId}`);

    } catch (error) {
      hideLoading();
      console.error('Failed to reset SLA:', error);
      showError('Failed to Reset', error.response?.data?.message || 'An error occurred while resetting the SLA.', `resetSlaError-${categoryId}`);
    } finally {
      setSaving(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const columns = [
    {
      title: 'Ticket Category',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      // FIXED: Use flexbox to vertically center the text
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Response Time',
      key: 'responseTime',
      width: 220,
      // FIXED: Use flexbox to vertically center the form items
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Row gutter={8} style={{ width: '100%' }}>
            <Col span={12}>
              <Form.Item
                name={`agreedResponseTimeValue-${record.id}`}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 1, message: 'Must be ≥1' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Hours"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={`agreedResponseTimeUnit-${record.id}`}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Unit" style={{ width: '100%' }}>
                  <Option value="minute">Minutes</Option>
                  <Option value="hour">Hours</Option>
                  <Option value="day">Days</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      title: 'Resolution Time',
      key: 'resolutionTime',
      width: 220,
      // FIXED: Use flexbox to vertically center the form items
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Row gutter={8} style={{ width: '100%' }}>
           <Col span={12}>
              <Form.Item
                name={`agreedResolutionTimeValue-${record.id}`}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 1, message: 'Must be ≥1' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Hours"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={`agreedResolutionTimeUnit-${record.id}`}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Unit" style={{ width: '100%' }}>
                  <Option value="minute">Minutes</Option>
                  <Option value="hour">Hours</Option>
                  <Option value="day">Days</Option>
              </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      title: 'Priority',
      key: 'priority',
      width: 180,
      // FIXED: Use flexbox to vertically center the form item
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Form.Item
            name={`priorityId-${record.id}`}
            style={{ marginBottom: 0, width: '100%' }}
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              placeholder="Select"
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {priorities.map((p) => (
                <Option key={p.id} value={p.id} label={p.name}>
                  {p.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      // FIXED: Use flexbox to vertically center the buttons
      render: (_, record) => {
        const isSaving = saving[record.id];
        const isSaved = customSlas[record.id]?.isSaved;
        return (
          <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Space size="small">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => handleSaveSla(record.id)}
                loading={isSaving}
                disabled={isSaving}
                size="middle"
              />
              {isSaved && (
                <Popconfirm
                  title={`Reset SLA for ${record.name}?`}
                  onConfirm={() => handleResetSla(record.id)}
                  okText="Yes"
                  cancelText="No"
                  disabled={isSaving}
                >
                  <Button
                    icon={<CloseOutlined />}
                    danger
                    loading={isSaving}
                    disabled={isSaving}
                    size="middle"
                  />
                </Popconfirm>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      align: 'center',
      // FIXED: Use flexbox to vertically and horizontally center the icon
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {customSlas[record.id]?.isSaved ? (
            <CheckOutlined style={{ color: '#52c41a', fontSize: 18 }} />
          ) : (
            <CloseOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            Manage Custom SLAs for {contractName}
          </span>
        </div>
      }
      onCancel={onCancel}
      footer={null}
      width={1100}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={ticketCategories}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
            size="middle"
            style={{ marginTop: 16 }}
          />
        </Form>
      </Spin>
    </Modal>
  );
};

export default ContractSlaManagementModal;